// @flow

import io from 'socket.io-client';
import logger from '../logger';
import api from './api';

const websocketLogger = logger.child({ category: 'websocket' });

// Get the realtime base URL with fallback
const getRealtimeUrl = () => {
	const url = process.env.REACT_APP_REALTIME_BASE_URL;
	if (!url) {
		websocketLogger.error('REACT_APP_REALTIME_BASE_URL is not defined in environment variables');
		// Fallback URL or throw error based on your needs
		return 'https://manager.hvend.hu:8091'; // Using production URL as fallback
	}
	return url;
};

// Lazy socket initialization
let socket = null;
let isConnected = false;
let connectionPromise = null;

const getSocket = () => {
	if (!socket) {		
		const serverUrl = getRealtimeUrl();
		websocketLogger.info(`Initializing socket connection to: ${serverUrl}`);
		
		socket = io(serverUrl, { 
			transports: ['websocket', 'polling'], // Fallback to polling if websocket fails
			path: '/',
			autoConnect: true,
			reconnection: true,
			reconnectionDelay: 1000,
			reconnectionDelayMax: 5000,
			maxReconnectionAttempts: 5,
			timeout: 20000,
			forceNew: true
		});

		// Add connection event handlers
		socket.on('connect', () => {
			isConnected = true;
			websocketLogger.info('Socket connected successfully');
		});

		socket.on('disconnect', (reason) => {
			isConnected = false;
			websocketLogger.warn(`Socket disconnected: ${reason}`);
		});

		socket.on('connect_error', (error) => {
			isConnected = false;
			websocketLogger.error('Socket connection error:', error);
		});

		socket.on('reconnect', (attemptNumber) => {
			isConnected = true;
			websocketLogger.info(`Socket reconnected after ${attemptNumber} attempts`);
		});

		socket.on('reconnect_error', (error) => {
			websocketLogger.error('Socket reconnection error:', error);
		});

		socket.on('reconnect_failed', () => {
			websocketLogger.error('Socket reconnection failed - all attempts exhausted');
		});
	}
	return socket;
};

type Handler = (Object, Object) => void | Promise<mixed>;
export type Subscription = {
	unsubscribe: () => void
};

function subscribeRoom(room, isJSON = true) {
	return (handler: Handler): Subscription => {
		const callback = (data, params) => {
			try {
				const jsonData = JSON.parse(data);
				handler(isJSON ? jsonData : data, params);
			} catch (error) {
				handler(data, params);
			}
		};

		const socket = getSocket();
		
		// Wait for connection before subscribing
		const doSubscribe = () => {
			if (!socket.connected) {
				websocketLogger.debug(`Waiting for connection before subscribing to ${room}`);
				socket.once('connect', () => {
					socket.emit('subscribe', room, api.getToken());
					websocketLogger.debug(`Subscribe ${room} (after connection)`);
				});
			} else {
				socket.emit('subscribe', room, api.getToken());
				websocketLogger.debug(`Subscribe ${room} (already connected)`);
			}
			
			socket.on(room, callback);
		};

		doSubscribe();

		return {
			unsubscribe: () => {
				const socket = getSocket();
				socket.emit('unsubscribe', room);
				socket.off(room, callback);
				websocketLogger.debug(`Unsubscribe ${room}`);
			}
		};
	};
}

function send(event, data) {
	websocketLogger.debug(`Send event (${event}): ${JSON.stringify(data)}`);
	const socket = getSocket();
	
	if (!socket.connected) {
		websocketLogger.debug(`Socket not connected, queuing event: ${event}`);
		socket.once('connect', () => {
			socket.emit(event, data);
			websocketLogger.debug(`Sent queued event (${event}) after connection`);
		});
	} else {
		socket.emit(event, data);
	}
}

export default {
	remoteCommands: {
		takeScreenshot: 'takeScreenshot',
		setInfoText: 'setInfoText',
		setLayout: 'setLayout',
		getLogFile: 'getLogFile',
		startDebug: 'startDebug',
		stopDebug: 'stopDebug',
		rebootRunner: 'reboot',
		startRemotePriceChecking: 'startRemotePriceChecking',
		getRemotePriceCheckProducts: 'getRemotePriceCheckProducts'
	},

	// Connection utilities
	isConnected: () => socket && socket.connected,
	getConnectionState: () => ({
		connected: socket ? socket.connected : false,
		socketId: socket ? socket.id : null
	}),
	
	// Force reconnection
	reconnect: () => {
		if (socket) {
			websocketLogger.info('Forcing socket reconnection');
			socket.disconnect();
			socket.connect();
		}
	},

	subscribeKeepAlive: subscribeRoom('keepAlive'),
	subscribeStatusReport: subscribeRoom('statusReports'),
	subscribeProductInfo: subscribeRoom('productInfo'),
	subscribeStatusReportForVM: (hardwareID: string, handler: Handler) =>
		subscribeRoom(`machine/${hardwareID}/statusReport`, false)(handler),
	subscribeSale: (hardwareID: string, handler: Handler) =>
		subscribeRoom(`machine/${hardwareID}/sale`, false)(handler),
	subscribeProductScanForVM: (hardwareID: string, handler: Handler) =>
		subscribeRoom(`machine/${hardwareID}/productScan`)(handler),
	subscribeProductUpdateForVM: (hardwareID: string, handler: Handler) =>
		subscribeRoom(`machine/${hardwareID}/productReset`, false)(handler),
	subscribeProductPriceResponseForVM: (hardwareID: string, handler: Handler) =>
		subscribeRoom(`machine/${hardwareID}/priceAck`)(handler),
	subscribeProductPromoResponseForVM: (hardwareID: string, handler: Handler) =>
		subscribeRoom(`machine/${hardwareID}/promoAck`)(handler),
	subscribeProductInfoForVM: (hardwareID: string, handler: Handler) =>
		subscribeRoom(`machine/${hardwareID}/productInfo`)(handler),
	subscribeLCDForVM: (hardwareID: string, handler: Handler) =>
		subscribeRoom(`machine/${hardwareID}/lcd`, false)(handler),
	subscribeForSugarLamps: (hardwareID: string, handler: Handler) =>
		subscribeRoom(`machine/${hardwareID}/sugarlamps`, false)(handler),
	subscribeLCDTimeoutForVM: (hardwareID: string, handler: Handler) =>
		subscribeRoom(`machine/${hardwareID}/lcdTimeout`, false)(handler),
	subscribeLayoutRefresh: (hardwareID: string, handler: Handler) =>
		subscribeRoom(`machine/${hardwareID}/layoutRefresh`)(handler),
	subscribeCommandResult: (hardwareID: string, handler: Handler) =>
		subscribeRoom(`machine/${hardwareID}/commandResult`)(handler),

	turnOnLCD: (hardwareID: string) => {		
		send('lcdStatus', {
			hardwareID: hardwareID,
			jwtToken: api.getToken(),
			status: 'on'
		})
	},
	turnOffLCD: (hardwareID: string) =>
		send('lcdStatus', {
			hardwareID: hardwareID,
			jwtToken: api.getToken(),
			status: 'off'
		}),
	turnOnRemoteControl: (hardwareID: string) => {		
		send('lcdRemote', {
			hardwareID: hardwareID,
			jwtToken: api.getToken(),
			status: 'on'
		});
	},
	turnOffRemoteControl: (hardwareID: string) =>
		send('lcdRemote', {
			hardwareID: hardwareID,
			jwtToken: api.getToken(),
			status: 'off'
		}),
	pressButton: (hardwareID: string, buttonId: string, slavePrefix: string) => {
		send('buttonPress', {
			hardwareID: hardwareID,
			jwtToken: api.getToken(),
			buttonId,
			slavePrefix
		});
	},
	sendCommand: (hardwareID: string, command, parameter) => {
		send('remoteCommand', {
			hardwareID: hardwareID,
			jwtToken: api.getToken(),
			commandString: command,
			parameter
		});
	},
	setPromotion: (hardwareID: string, buttonId: string, promotion: Object) =>
		send('setPromotion', {
			hardwareID: hardwareID,
			jwtToken: api.getToken(),
			buttonId,
			promotion
		}),
	updateProductList: (hardwareID: string) =>
		send('updateProductList', {
			hardwareID: hardwareID,
			jwtToken: api.getToken()
		}),
	updateProductPrice: (
		hardwareID: string,
		buttonId: string,
		price: number,
		slavePattern: ?string
	) =>
		send('updateProductPrice', {
			hardwareID: hardwareID,
			jwtToken: api.getToken(),
			buttonId,
			price,
			slavePattern
		}),

	updateMachineName: (hardwareID: string, machineName: string) => {
		send('updateMachineName', { hardwareID, machineName });
	},

	sendMMUCommand: (partSerialNumber: string, commandText: string) => {
		send('mmuCommand', {
			hardwarePartSerial: partSerialNumber,
			jwtToken: api.getToken(),
			commandText
		});
	}
};
